import { Parser } from '~/api';
import { Tag } from '~/api/models';
import RequestService from './request';

interface CreateTagRequest {
    name: string;
    slug: string;
}

type UpdateTagRequest = CreateTagRequest;

interface DeleteTagRequest {
    id: string;
}

class TagsService {
    getAll(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/tags', {
            ...req,
            include: ['nodesCount', 'eggsCount'],
        }).then(Parser.parse);
    }

    /**
     * Load every tag by walking the pages — used to seed the tag picker, whose
     * static option list needs the full set (per_page is capped at 50 server-side).
     */
    async getAllForPicker(): Promise<Tag[]> {
        const collected: Tag[] = [];
        let page = 1;
        let totalPages = 1;

        do {
            const response = await this.getAll({ perPage: 50, page } as PaginatableRequest);
            collected.push(...((response.data ?? []) as unknown as Tag[]));
            totalPages = response.meta?.pagination?.totalPages ?? 1;
            page++;
        } while (page <= totalPages);

        return collected;
    }

    get(): Promise<Tag> {
        return RequestService.get('/tags/:tag', {
            include: ['nodes', 'eggs'],
        }).then(Parser.parse);
    }

    create(data: CreateTagRequest): Promise<Tag> {
        return RequestService.post('/tags', data).then(Parser.parse);
    }

    update(data: UpdateTagRequest): Promise<Tag> {
        return RequestService.put('/tags/:tag', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    delete(data: DeleteTagRequest): Promise<void> {
        return RequestService.delete(`/tags/${data.id}`);
    }

    pruneUnused(): Promise<void> {
        return RequestService.delete('/tags/unused');
    }

    syncNodes(data: { nodes: string[] }): Promise<ListResponse> {
        return RequestService.put('/tags/:tag/nodes', data).then(Parser.parse);
    }

    syncEggs(data: { eggs: string[] }): Promise<ListResponse> {
        return RequestService.put('/tags/:tag/eggs', data).then(Parser.parse);
    }
}

export default new TagsService();
